<template>
  <v-main class="student-course">
    <div v-if="isLessonLoading" class="student-course__loading">
      <v-progress-circular class="theme-loader" size="64" width="5" indeterminate />
      <p>Завантажуємо предмет…</p>
    </div>

    <div v-else class="student-course__inner">
      <section
        class="student-course__hero theme-dashboard-banner"
        :class="{ 'student-course__hero--with-mascot': mascotVisible }"
      >
        <div class="student-course__identity">
          <span class="student-course__icon">
            <v-icon color="#2d68bd" size="31">
              {{ courseIcon || 'mdi-book-open-page-variant-outline' }}
            </v-icon>
          </span>
          <div>
            <span class="student-course__eyebrow">Навчальний предмет</span>
            <h1>{{ courseName }}</h1>
            <p>{{ user.grade ? `${user.grade}-й клас` : 'Клас не вказано' }}</p>
          </div>
        </div>

        <div
          class="student-course__teacher"
          v-if="isGuest || teacherName || teacher.email"
        >
          <span><v-icon size="23">mdi-account-outline</v-icon></span>
          <div>
            <small>{{ isGuest ? 'Викладач' : 'Вчитель' }}</small>
            <strong>{{ isGuest ? 'Команда RedFox' : teacherName || 'Не вказано' }}</strong>
            <span v-if="isGuest" class="student-course__teacher-demo">
              Демонстраційний профіль
            </span>
            <a
              v-else-if="teacher.email"
              :href="`mailto:${teacher.email}?subject=${courseName}`"
            >
              {{ teacher.email }}
            </a>
          </div>
        </div>

        <div class="student-course__progress">
          <div class="student-course__progress-head">
            <div>
              <small>Прогрес курсу</small
              ><strong
                >{{ passedLessons }} з {{ progressLessons.length }} уроків</strong
              >
            </div>
            <span>{{ progressPercent }}%</span>
          </div>
          <div
            class="student-course__progress-track"
            role="progressbar"
            :aria-valuenow="progressPercent"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <span :style="{ width: `${progressPercent}%` }"></span>
          </div>
        </div>
        <ThemeMascot
          class="student-course__mascot"
          @visibility-change="mascotVisible = $event"
        />
      </section>

      <section v-if="newLessons.length" class="student-course__section">
        <header class="student-course__section-head">
          <div>
            <h2>Продовжити навчання</h2>
            <p>Наступні уроки за програмою</p>
          </div>
        </header>
        <div class="student-course__next-grid">
          <button
            v-for="lesson in newLessons"
            :key="lesson.id"
            type="button"
            class="next-lesson"
            @click="handleClick(lesson)"
          >
            <span class="next-lesson__number">Урок {{ lesson.index + 1 }}</span>
            <h3>{{ lesson.name }}</h3>
            <div class="next-lesson__footer">
              <span
                class="lesson-status"
                :class="`lesson-status--${statusTheme(lesson)}`"
              >
                {{ lesson.statusLabel || 'Не розпочато' }}
              </span>
              <v-icon color="#667085" size="21">mdi-arrow-right</v-icon>
            </div>
          </button>
        </div>
      </section>

      <section
        v-if="!isGuest"
        class="student-course__section student-course__journal"
      >
        <header class="student-course__section-head">
          <div>
            <h2>Мої оцінки</h2>
            <p>Результати за пройдені уроки</p>
          </div>
        </header>
        <div class="student-course__journal-content">
          <TeacherJournal
            :isTeacher="false"
            :source-store="sourceStoreName"
            @journalEvent="journalEventHandler"
          />
        </div>
      </section>

      <section class="student-course__section student-course__lessons">
        <header
          class="student-course__section-head student-course__section-head--actions"
        >
          <div>
            <h2>Усі уроки</h2>
            <p>{{ lessonCountLabel }}</p>
          </div>
          <div class="student-course__lesson-actions">
            <div class="student-course__search">
              <v-icon color="#7a8592" size="20">mdi-magnify</v-icon>
              <input
                v-model.trim="lessonSearch"
                type="search"
                placeholder="Знайти урок"
                aria-label="Знайти урок"
              />
              <button
                v-if="lessonSearch"
                type="button"
                aria-label="Очистити пошук"
                @click="lessonSearch = ''"
              >
                <v-icon color="#7a8592" size="18">mdi-close</v-icon>
              </button>
            </div>
            <button
              type="button"
              class="student-course__materials"
              @click="toLibrary"
            >
              <v-icon size="20">mdi-bookshelf</v-icon>
              Підручники та матеріали
            </button>
          </div>
        </header>

        <div
          class="student-course__lesson-filters"
          role="group"
          aria-label="Фільтр уроків за станом"
        >
          <span>Показати:</span>
          <button
            v-for="filter in lessonStateFilters"
            :key="filter.value"
            type="button"
            :class="{
              'student-course__lesson-filter--active':
                lessonStateFilter === filter.value,
            }"
            @click="lessonStateFilter = filter.value"
          >
            {{ filter.label }}
            <small>{{ lessonStateCount(filter.value) }}</small>
          </button>
        </div>

        <div v-if="filteredLessons.length" class="student-course__table-wrap">
          <table>
            <thead>
              <tr>
                <th>№</th>
                <th>Тема уроку</th>
                <th>Оцінка</th>
                <th>Статус</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="lesson in filteredLessons"
                :key="lesson.id"
                tabindex="0"
                :class="{
                  'student-course__lesson--preview': isPreviewLesson(lesson),
                }"
                @click="handleClick(lesson)"
                @keydown.enter="handleClick(lesson)"
              >
                <td>{{ lesson.index + 1 }}</td>
                <td>
                  <span class="student-course__lesson-title">
                    <v-icon
                      v-if="isPreviewLesson(lesson)"
                      class="student-course__lesson-lock"
                      color="#7a8592"
                      size="20"
                    >
                      mdi-lock-outline
                    </v-icon>
                    <strong>{{ lesson.name }}</strong>
                  </span>
                  <span
                    v-if="isGuest"
                    class="student-course__availability"
                    :class="{
                      'student-course__availability--demo': lesson.isDemo,
                    }"
                  >
                    {{ lesson.isDemo ? 'Доступний у демо' : 'Повна програма' }}
                  </span>
                  <span
                    v-if="lesson.hasMessages"
                    class="student-course__message"
                    >Нове повідомлення</span
                  >
                </td>
                <td>
                  <span class="student-course__grade">{{
                    lesson.evaluation || '—'
                  }}</span>
                </td>
                <td>
                  <span v-if="isPreviewLesson(lesson)" class="lesson-status lesson-status--preview">
                    Повна програма
                  </span>
                  <span
                    v-else
                    class="lesson-status"
                    :class="`lesson-status--${statusTheme(lesson)}`"
                    >{{ lesson.statusLabel || 'Не розпочато' }}</span
                  >
                </td>
                <td>
                  <v-icon
                    v-if="!isPreviewLesson(lesson)"
                    color="#7a8592"
                    size="20"
                  >mdi-chevron-right</v-icon>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="student-course__empty">
          <template v-if="hasLessonFilters">
            <v-icon color="#7a8592" size="30">mdi-magnify-close</v-icon>
            <strong>Уроків не знайдено</strong>
            <span>Спробуй змінити пошук або фільтр.</span>
            <button type="button" @click="resetLessonFilters">
              Скинути фільтри
            </button>
          </template>
          <template v-else>У цьому курсі поки немає уроків.</template>
        </div>
      </section>
    </div>
  </v-main>
</template>

<script>
import statesEnum from '@/enums/statesEnum';
import TeacherJournal from '@/components/View/TeacherJournal/TeacherJournal';
import ThemeMascot from '@/components/Base/ThemeMascot/ThemeMascot';

export default {
  name: 'ClassPage',
  components: { TeacherJournal, ThemeMascot },
  data: () => ({
    sourceStoreName: 'TeacherRoomStore',
    lessonSearch: '',
    lessonStateFilter: 'all',
    mascotVisible: false,
  }),
  computed: {
    isLessonLoading() {
      return this.$store.getters['LessonsStore/isLessonLoading'];
    },
    isGuest() {
      return this.$store.getters['UserStore/isGuest']();
    },
    lessons() {
      return this.$store.getters['LessonsStore/getLessons'];
    },
    filteredLessons() {
      const query = this.lessonSearch.toLocaleLowerCase('uk');
      return this.lessons.filter((lesson) => {
        const matchesState = this.lessonMatchesFilter(
          lesson,
          this.lessonStateFilter
        );
        const searchableValue = [
          lesson.index + 1,
          lesson.name,
          lesson.statusLabel || 'Не розпочато',
        ]
          .join(' ')
          .toLocaleLowerCase('uk');
        return matchesState && (!query || searchableValue.includes(query));
      });
    },
    lessonCountLabel() {
      if (!this.hasLessonFilters)
        return `${this.lessons.length} уроків у курсі`;
      return `Знайдено ${this.filteredLessons.length} з ${this.lessons.length}`;
    },
    hasLessonFilters() {
      return Boolean(this.lessonSearch || this.lessonStateFilter !== 'all');
    },
    passedLessons() {
      return this.progressLessons.filter((lesson) => lesson.status === 3).length;
    },
    progressLessons() {
      return this.isGuest
        ? this.lessons.filter((lesson) => lesson.isDemo)
        : this.lessons;
    },
    progressPercent() {
      return this.progressLessons.length
        ? Math.round((this.passedLessons / this.progressLessons.length) * 100)
        : 0;
    },
    newLessons() {
      const availableLessons = this.isGuest ? this.progressLessons : this.lessons;
      const activeIndex = availableLessons.findIndex((lesson) =>
        [1, 2, 4].includes(lesson.status)
      );
      if (activeIndex !== -1)
        return availableLessons.slice(activeIndex, activeIndex + 3);
      const notStartedIndex = availableLessons.findIndex(
        (lesson) => lesson.status === 0
      );
      if (notStartedIndex !== -1)
        return availableLessons.slice(notStartedIndex, notStartedIndex + 3);
      return [];
    },
    lessonStateFilters() {
      if (this.isGuest) {
        return [
          { value: 'demo', label: 'Доступні в демо' },
          { value: 'all', label: 'Вся програма' },
        ];
      }
      return [
        { value: 'all', label: 'Усі' },
        { value: 'unfinished', label: 'Незавершені' },
        { value: 'not-started', label: 'Не розпочаті' },
        { value: 'in-progress', label: 'На вивченні' },
        { value: 'need-check', label: 'На перевірці' },
        { value: 'rework', label: 'Треба доробити' },
        { value: 'completed', label: 'Завершені' },
        { value: 'messages', label: 'З повідомленнями' },
      ];
    },
    teacher() {
      return this.$store.getters['LessonsStore/getTeacher'];
    },
    teacherName() {
      return [this.teacher.firstName, this.teacher.lastName]
        .filter(Boolean)
        .join(' ');
    },
    courseName() {
      return this.$store.getters['LessonsStore/getCourseName'];
    },
    courseIcon() {
      return this.$store.getters['LessonsStore/getCourseIcon'];
    },
    user() {
      return this.$store.getters['UserStore/getUser'];
    },
  },
  created() {
    this.lessonStateFilter = this.isGuest ? 'demo' : 'all';
    this.loadLessonsFromRoute();
  },
  watch: {
    '$route.query.courseId'(courseId, previousCourseId) {
      if (courseId && courseId !== previousCourseId)
        this.loadLessonsFromRoute();
    },
  },
  methods: {
    toLibrary() {
      this.$router.push({ name: statesEnum.LIBRARY });
    },
    loadLessonsFromRoute() {
      const courseId = this.$route.query.courseId;
      if (courseId)
        this.$store.dispatch('LessonsStore/loadLessons', { courseId });
    },
    journalEventHandler() {},
    lessonStateCount(filter) {
      return this.lessons.filter((lesson) =>
        this.lessonMatchesFilter(lesson, filter)
      ).length;
    },
    lessonMatchesFilter(lesson, filter) {
      const filters = {
        all: () => true,
        demo: () => lesson.isDemo,
        unfinished: () => lesson.status !== 3,
        'not-started': () => lesson.status === 0,
        'in-progress': () => lesson.status === 1,
        'need-check': () => lesson.status === 2,
        rework: () => lesson.status === 4,
        completed: () => lesson.status === 3,
        messages: () => Boolean(lesson.hasMessages),
      };
      return (filters[filter] || filters.all)();
    },
    resetLessonFilters() {
      this.lessonSearch = '';
      this.lessonStateFilter = 'all';
    },
    async handleClick(lesson) {
      const isPreview = this.isPreviewLesson(lesson);
      if (isPreview) {
        await this.$store.dispatch('CurrentLessonStore/setLessonPreview', {
          name: lesson.name,
          courseName: this.courseName,
          courseId: this.$route.query.courseId,
          gradeNumber: lesson.gradeNumber || this.user.grade,
        });
      }
      const { preview, ...query } = this.$route.query;
      try {
        await this.$router.push({
          name: statesEnum.LESSON,
          params: { lessonId: lesson.id },
          query: isPreview ? { ...query, preview: '1' } : query,
        });
      } catch (error) {
        if (!error || error.name !== 'NavigationDuplicated') throw error;
      }
    },
    isPreviewLesson(lesson) {
      return this.isGuest && !lesson.isDemo;
    },
    statusTheme(lesson) {
      const themes = {
        0: 'neutral',
        1: 'progress',
        2: 'check',
        3: 'done',
        4: 'rework',
      };
      if (lesson.className === 'not-confirmed') return 'danger';
      return themes[lesson.status] || 'neutral';
    },
  },
};
</script>

<style lang="less" scoped>
.student-course {
  background: var(--theme-app-background-accent);
  min-height: 100vh;
}
.student-course__inner {
  margin: 0 auto;
  max-width: 1480px;
  padding: 34px 28px 60px;
}
.student-course__loading {
  align-items: center;
  color: #667085;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: calc(100vh - 64px);
}
.student-course__loading p {
  margin-top: 16px;
}
.student-course__hero {
  align-items: center;
  border: 1px solid var(--theme-banner-border);
  border-radius: 18px;
  box-shadow: 0 8px 30px rgba(31, 41, 55, 0.07);
  display: grid;
  gap: 18px;
  grid-template-columns:
    minmax(260px, 1fr) minmax(220px, 0.75fr) minmax(240px, 0.8fr);
  margin-bottom: 22px;
  padding: 24px;
}
.student-course__hero--with-mascot {
  grid-template-columns:
    minmax(210px, 1.1fr) minmax(180px, 0.8fr)
    minmax(220px, 0.9fr) 132px;
}
.student-course__mascot {
  height: 132px;
  justify-self: end;
  width: 132px;
}
.student-course__identity {
  align-items: center;
  display: flex;
  gap: 17px;
  min-width: 0;
}
.student-course__icon {
  align-items: center;
  background: #eef4fc;
  border-radius: 15px;
  display: flex;
  flex: 0 0 auto;
  height: 60px;
  justify-content: center;
  width: 60px;
}
.student-course__eyebrow {
  color: #2d68bd;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.student-course__identity h1 {
  color: #172033;
  font-size: 29px;
  line-height: 1.2;
  margin: 4px 0 5px;
}
.student-course__identity p {
  color: #667085;
  margin: 0;
}
.student-course__teacher {
  align-items: center;
  border-left: 1px solid var(--theme-border-decorative);
  display: flex;
  gap: 12px;
  min-width: 0;
  padding-left: 24px;
}
.student-course__teacher > span {
  align-items: center;
  background: var(--theme-surface-decorative);
  border-radius: 11px;
  display: flex;
  flex: 0 0 auto;
  height: 44px;
  justify-content: center;
  width: 44px;
}
.student-course__teacher > span::v-deep .v-icon {
  color: var(--theme-banner-text) !important;
}
.student-course__teacher small,
.student-course__teacher strong,
.student-course__teacher a,
.student-course__teacher-demo {
  display: block;
}
.student-course__teacher small {
  color: #7a8592;
}
.student-course__teacher strong {
  color: #172033;
  margin: 2px 0;
}
.student-course__teacher a {
  color: #2d68bd;
  font-size: 12px;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.student-course__teacher-demo {
  color: #667085;
  font-size: 12px;
  margin-top: 2px;
}
.student-course__progress {
  background: #fff;
  border: 1px solid var(--theme-border-decorative);
  border-radius: 13px;
  padding: 15px;
}
.student-course__progress-head {
  align-items: center;
  display: flex;
  justify-content: space-between;
}
.student-course__progress-head small,
.student-course__progress-head strong {
  display: block;
}
.student-course__progress-head small {
  color: #7a8592;
}
.student-course__progress-head strong {
  color: #172033;
  margin-top: 3px;
}
.student-course__progress-head > span {
  color: var(--theme-banner-text);
  font-size: 22px;
  font-weight: 900;
}
.student-course__progress-track {
  background: var(--theme-surface-decorative);
  border-radius: 999px;
  height: 8px;
  margin-top: 12px;
  overflow: hidden;
}
.student-course__progress-track span {
  background: var(--theme-accent-decorative);
  border-radius: inherit;
  display: block;
  height: 100%;
}
.student-course__section {
  background: #fff;
  border: 1px solid #e4e8eb;
  border-radius: 18px;
  box-shadow: 0 8px 30px rgba(31, 41, 55, 0.06);
  margin-bottom: 22px;
  padding: 22px;
}
.student-course__section-head {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
}
.student-course__section-head h2 {
  color: #172033;
  font-size: 22px;
  margin: 0 0 4px;
}
.student-course__section-head p {
  color: #667085;
  margin: 0;
}
.student-course__next-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.next-lesson {
  background: #fff;
  border: 1px solid #e3e8eb;
  border-radius: 14px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  min-height: 170px;
  padding: 18px;
  text-align: left;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}
.next-lesson:hover {
  border-color: #bcd8cd;
  box-shadow: 0 9px 22px rgba(31, 41, 55, 0.09);
  transform: translateY(-2px);
}
.next-lesson__number {
  color: #2d68bd;
  font-size: 12px;
  font-weight: 850;
  text-transform: uppercase;
}
.next-lesson h3 {
  color: #172033;
  display: -webkit-box;
  font-size: 17px;
  line-height: 1.45;
  margin: 12px 0;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}
.next-lesson__footer {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: auto;
}
.lesson-status {
  border-radius: 999px;
  display: inline-flex;
  font-size: 11px;
  font-weight: 800;
  padding: 5px 9px;
  white-space: nowrap;
}
.lesson-status--neutral {
  background: #f0f2f4;
  color: #667085;
}
.lesson-status--progress {
  background: #eef4fc;
  color: #2d68bd;
}
.lesson-status--check {
  background: #fff3df;
  color: #a55d00;
}
.lesson-status--done {
  background: #e8f6ee;
  color: #207149;
}
.lesson-status--rework {
  background: #fff0dd;
  color: #a55400;
}
.lesson-status--danger {
  background: #ffe9e9;
  color: #b93131;
}
.lesson-status--preview {
  background: #f2f0fb;
  color: #67569a;
}
.student-course__journal {
  overflow: hidden;
}
.student-course__journal .student-course__section-head {
  margin-bottom: 8px;
}
.student-course__journal-content {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  width: 100%;
}
.student-course__journal-content::v-deep .teacher-journal {
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
  width: 100%;
}
.student-course__journal-content::v-deep .journal-filter-controls,
.student-course__journal-content::v-deep .tbl {
  box-sizing: border-box;
  max-width: 100%;
}
.student-course__journal-content::v-deep .tbl {
  box-shadow: none;
}
.student-course__materials {
  align-items: center;
  background: #fff;
  border: 1px solid var(--theme-accent-decorative);
  border-radius: 10px;
  color: var(--theme-banner-text);
  cursor: pointer;
  display: flex;
  font-weight: 800;
  gap: 8px;
  min-height: 43px;
  padding: 9px 14px;
}
.student-course__materials::v-deep .v-icon {
  color: var(--theme-banner-text) !important;
}
.student-course__materials:hover {
  background: var(--theme-surface-decorative);
}
.student-course__lesson-actions {
  align-items: center;
  display: flex;
  gap: 10px;
}
.student-course__search {
  align-items: center;
  background: #f8faf9;
  border: 1px solid #dfe5e2;
  border-radius: 10px;
  display: flex;
  gap: 8px;
  min-height: 43px;
  padding: 0 11px;
  width: 270px;
}
.student-course__search:focus-within {
  border-color: #79bda3;
  box-shadow: 0 0 0 3px rgba(35, 147, 111, 0.1);
}
.student-course__search input {
  background: transparent;
  border: 0;
  color: #172033;
  font: inherit;
  min-width: 0;
  outline: 0;
  width: 100%;
}
.student-course__search > button {
  align-items: center;
  background: transparent;
  border: 0;
  cursor: pointer;
  display: flex;
  padding: 2px;
}
.student-course__lesson-filters {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin: -2px 0 16px;
}
.student-course__lesson-filters > span {
  color: #667085;
  font-size: 13px;
  font-weight: 700;
  margin-right: 3px;
}
.student-course__lesson-filters > button {
  align-items: center;
  background: #f5f7f8;
  border: 1px solid #e0e5e8;
  border-radius: 999px;
  color: #596574;
  cursor: pointer;
  display: flex;
  font-size: 12px;
  font-weight: 800;
  gap: 6px;
  min-height: 34px;
  padding: 6px 10px;
}
.student-course__lesson-filters > button:hover {
  border-color: var(--theme-accent-decorative);
}
.student-course__lesson-filters > button small {
  align-items: center;
  background: #fff;
  border-radius: 999px;
  display: flex;
  font-size: 10px;
  height: 20px;
  justify-content: center;
  min-width: 20px;
  padding: 0 5px;
}
.student-course__lesson-filters .student-course__lesson-filter--active {
  background: var(--theme-surface-decorative);
  border-color: var(--theme-accent-decorative);
  color: var(--theme-banner-text);
}
.student-course__lesson-filters
  .student-course__lesson-filter--active
  small {
  background: var(--theme-banner-background);
  color: var(--theme-banner-text);
}
.student-course__table-wrap {
  border: 1px solid #e5e9ec;
  border-radius: 12px;
  overflow-x: auto;
}
.student-course table {
  border-collapse: collapse;
  min-width: 760px;
  width: 100%;
}
.student-course th,
.student-course td {
  border-bottom: 1px solid #e8ebee;
  padding: 13px 12px;
}
.student-course th {
  background: #f7f9fa;
  color: #596574;
  font-size: 12px;
  text-align: left;
  text-transform: uppercase;
}
.student-course th:not(:nth-child(2)),
.student-course td:not(:nth-child(2)) {
  text-align: center;
}
.student-course tbody tr {
  cursor: pointer;
}
.student-course tbody tr:hover {
  background: #fafcfb;
}
.student-course tbody .student-course__lesson--preview {
  background: #fcfbff;
}
.student-course tbody .student-course__lesson--preview:hover {
  background: #f7f4fc;
}
.student-course tbody tr:last-child td {
  border-bottom: 0;
}
.student-course__lesson-lock {
  flex: 0 0 auto;
}
.student-course__lesson-title {
  align-items: center;
  display: inline-flex;
  gap: 7px;
}
.student-course td:nth-child(2) strong {
  color: #172033;
}
.student-course__availability {
  background: #f2f0fb;
  border-radius: 999px;
  color: #67569a;
  display: inline-block;
  font-size: 10px;
  font-weight: 800;
  margin-left: 8px;
  padding: 3px 7px;
}
.student-course__availability--demo {
  background: #e8f6ee;
  color: #207149;
}
.student-course__grade {
  align-items: center;
  background: #f1f3f5;
  border-radius: 8px;
  display: inline-flex;
  font-weight: 850;
  justify-content: center;
  min-height: 32px;
  min-width: 36px;
}
.student-course__message {
  background: #ffe9e9;
  border-radius: 999px;
  color: #b93131;
  display: inline-block;
  font-size: 10px;
  font-weight: 800;
  margin-left: 8px;
  padding: 3px 6px;
}
.student-course__empty {
  align-items: center;
  color: #667085;
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 40px;
  text-align: center;
}
.student-course__empty strong {
  color: #172033;
  font-size: 18px;
}
.student-course__empty button {
  background: #23936f;
  border: 0;
  border-radius: 9px;
  color: #fff;
  cursor: pointer;
  font-weight: 800;
  margin-top: 8px;
  padding: 9px 14px;
}
@media (max-width: 1050px) {
  .student-course__hero {
    grid-template-columns: 1fr 1fr;
  }
  .student-course__progress {
    grid-column: 1/-1;
  }
  .student-course__mascot {
    display: none;
  }
  .student-course__next-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 760px) {
  .student-course__inner {
    padding: 24px 14px 40px;
  }
  .student-course__hero {
    grid-template-columns: 1fr;
    padding: 18px;
  }
  .student-course__teacher {
    border-left: 0;
    border-top: 1px solid #dce6e1;
    padding: 16px 0 0;
  }
  .student-course__progress {
    grid-column: auto;
  }
  .student-course__identity h1 {
    font-size: 24px;
  }
  .student-course__section {
    padding: 16px;
  }
  .student-course__journal-content::v-deep .journal-filter-row {
    margin-bottom: 20px !important;
    width: 100%;
  }
  .student-course__journal-content::v-deep .journal-filter-column {
    display: block !important;
    width: 100%;
  }
  .student-course__journal-content::v-deep .journal-filter-controls {
    width: 100%;
  }
  .student-course__journal-content::v-deep .tbl {
    overflow-x: auto;
    overscroll-behavior-x: contain;
    width: 100%;
    -webkit-overflow-scrolling: touch;
  }
  .student-course__table-wrap {
    background: transparent;
    border: 0;
    border-radius: 0;
    overflow: visible;
  }
  .student-course table {
    display: block;
    min-width: 0;
  }
  .student-course thead {
    display: none;
  }
  .student-course tbody {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .student-course tbody tr {
    background: #fff;
    border: 1px solid #e2e7ea;
    border-radius: 12px;
    display: grid;
    grid-template-columns: 34px minmax(0, 1fr) auto 24px;
    padding: 13px 12px;
  }
  .student-course tbody tr:hover {
    background: #fafcfb;
    border-color: #bdd8cd;
  }
  .student-course tbody tr:focus-visible {
    border-color: #79bda3;
    outline: 3px solid rgba(35, 147, 111, 0.16);
  }
  .student-course td {
    border: 0;
    padding: 0;
    text-align: left !important;
  }
  .student-course td:first-child {
    color: #667085;
    font-size: 13px;
    grid-column: 1;
    grid-row: 1 / span 2;
    padding-top: 2px;
  }
  .student-course td:nth-child(2) {
    grid-column: 2 / span 2;
    grid-row: 1;
    min-width: 0;
  }
  .student-course td:nth-child(2) strong {
    display: block;
    line-height: 1.4;
    overflow-wrap: anywhere;
    white-space: normal;
  }
  .student-course td:nth-child(3),
  .student-course td:nth-child(4) {
    align-items: center;
    display: flex;
    grid-row: 2;
    margin-top: 10px;
  }
  .student-course td:nth-child(3) {
    grid-column: 2;
  }
  .student-course td:nth-child(3)::before {
    color: #7a8592;
    content: "Оцінка:";
    font-size: 11px;
    margin-right: 7px;
  }
  .student-course td:nth-child(4) {
    grid-column: 3;
    justify-self: end;
  }
  .student-course td:last-child {
    align-self: center;
    grid-column: 4;
    grid-row: 1 / span 2;
  }
  .student-course__grade {
    min-height: 26px;
    min-width: 30px;
  }
  .student-course__message {
    margin: 7px 0 0;
  }
  .student-course__next-grid {
    grid-template-columns: 1fr;
  }
  .student-course__section-head--actions {
    align-items: stretch;
    flex-direction: column;
    gap: 14px;
  }
  .student-course__lesson-actions {
    align-items: stretch;
    flex-direction: column;
  }
  .student-course__search,
  .student-course__materials {
    justify-content: center;
    width: 100%;
  }
}
@media (max-width: 420px) {
  .student-course tbody tr {
    grid-template-columns: 30px minmax(0, 1fr) 22px;
  }
  .student-course td:nth-child(2) {
    grid-column: 2;
  }
  .student-course td:nth-child(3) {
    grid-column: 2;
    grid-row: 2;
  }
  .student-course td:nth-child(4) {
    grid-column: 2;
    grid-row: 3;
    justify-self: start;
    margin-top: 8px;
  }
  .student-course td:last-child {
    grid-column: 3;
    grid-row: 1 / span 3;
  }
}
</style>
